import classNames from 'classnames';
import { PropsWithChildren, RefObject, useEffect } from 'react';

type Props = PropsWithChildren<{
  open: boolean;
  onClose: () => void;
  /**
   * Ref to a container that encloses BOTH the trigger pill and the dropdown
   * panel. Mouse-down events outside this container close the dropdown.
   * Targeting the wrapper (not just the panel) avoids a click-toggle race
   * where mousedown closes and then the pill button's onClick re-opens.
   */
  anchorRef: RefObject<HTMLElement>;
  onApply?: () => void;
  applyLabel?: string;
  /** Renders the panel without the Apply footer (used by the client picker
   *  which provides its own bottom bar). */
  hideApply?: boolean;
  className?: string;
  panelStyle?: React.CSSProperties;
}>;

const FilterDropdown = ({
  open,
  onClose,
  anchorRef,
  onApply,
  applyLabel = 'Apply selection',
  hideApply,
  className,
  panelStyle,
  children,
}: Props) => {
  useEffect(() => {
    if (!open) return;

    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (anchorRef.current && anchorRef.current.contains(target)) return;
      onClose();
    };

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    // Capture so we beat React's onClick handlers (otherwise another pill's
    // trigger click could fire between mousedown and re-open this one).
    document.addEventListener('mousedown', handleMouseDown, true);
    window.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown, true);
      window.removeEventListener('keydown', handleKey);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  return (
    <div
      className={classNames('dropdown-panel', className)}
      style={panelStyle}
      role="dialog"
    >
      {children}
      {!hideApply && onApply ? (
        <div className="dropdown-footer">
          <button type="button" className="dropdown-apply" onClick={onApply}>
            {applyLabel}
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default FilterDropdown;
